"use client";

import { useDebounce } from "@/hooks/useDebounce";
import { useEffect, useMemo, useState } from "react";
import ItemCard from "./ItemCard";
import ItemGrid from "./ItemGrid";
import SearchBar from "./SearchBar";
import SectionHeader from "./SectionHeader";

export default function SearchableListSection({
  items = [],
  onSearch,
  searchPlaceholder = "Ara...",
  title = "Öğeler",
  className = "",
  itemTitleKey = "Name",
  itemImageKey = "LogoUrl",
  itemFallbackImage = "/images/dummy/thy.jpg",
}) {
  const [searchTerm, setSearchTerm] = useState("");

  // 500ms debounce ekle (best practice)
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Filtrelenmiş öğeler - debounced search term kullan
  const filteredItems = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return items;

    return items.filter((item) => {
      if (typeof item === "string") {
        return item.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
      }

      // Hem Name hem de Slug üzerinden arama yap
      const name =
        item[itemTitleKey] || item.Name || item.title || item.name || "";
      const slug = item.Slug || item.slug || "";

      const searchTermLower = debouncedSearchTerm.toLowerCase();
      const nameLower = name.toLowerCase();
      const slugLower = slug.toLowerCase();

      return (
        nameLower.includes(searchTermLower) ||
        slugLower.includes(searchTermLower)
      );
    });
  }, [items, debouncedSearchTerm, itemTitleKey]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  // Debounced search term değiştiğinde onSearch callback'ini çağır
  useEffect(() => {
    if (onSearch) {
      onSearch(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onSearch]);

  // Varsayılan render fonksiyonu
  const defaultRenderItem = (item, index) => {
    if (typeof item === "string") {
      return (
        <div className="rounded-lg bg-white p-4 shadow-md transition-shadow duration-200 hover:shadow-lg">
          <h3 className="text-lg font-semibold text-gray-800">{item}</h3>
        </div>
      );
    }

    // ItemCard kullanarak render et
    const title = item.Name;
    const image = item[itemImageKey] || item.image || itemFallbackImage;
    const IataCode = item.Code;
    const airlineUrl = item.RedirectUrl;
    return (
      <ItemCard
        title={title}
        IataCode={IataCode}
        airlineUrl={airlineUrl}
        image={image}
        onClick={() => {}}
      />
    );
  };

  return (
    <section className={`w-full ${className}`}>
      <div className="container mx-auto flex flex-col gap-4 md:gap-6 lg:gap-8 xl:gap-10">
        <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
          <SectionHeader title={title} />
          <SearchBar placeholder={searchPlaceholder} onSearch={handleSearch} />
        </div>

        <ItemGrid items={filteredItems} renderItem={defaultRenderItem} />
      </div>
    </section>
  );
}
